import React, { useMemo } from "react";
import cn from "classnames";
import PropTypes from "prop-types";
import ReactSelect, { components } from "react-select";
import { useQuery } from "components/graphql";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import withStyles from "components/with-styles";
import styles from "./styles/autocomplete-select.css.js";

export const DEFAULT_AUTOCOMPLETE_LIMIT = 1000000;

const getQuery = ({ gqlQuery, variables }) => ({
  query: gqlQuery,
  variables: { offset: 0, limit: DEFAULT_AUTOCOMPLETE_LIMIT, ...variables },
});

const reactSelectStyles = {
  container: styles => ({
    ...styles,
    minHeight: 50,
    marginBottom: 8,
    marginTop: 34,
  }),
  singleValue: styles => ({
    margin: 0,
  }),
  control: styles => ({
    ...styles,
    backgroundColor: "white",
    borderRadius: 0,
    border: "none",
    borderBottom: "1px solid rgba(0, 0, 0, 0.42)",
    marginBottom: 13,
    fontSize: 16,
    boxShadow: "none",
    "&:hover": {
      borderColor: "rgba(0, 0, 0, 0.42)",
    },
  }),
  placeholder: styles => ({
    ...styles,
    left: 0,
    bottom: 0,
    opacity: 0.8,
    margin: 0,
    padding: 0,
  }),
  valueContainer: styles => ({
    ...styles,
    padding: 0,
  }),
  option: (styles, { data, isDisabled, isFocused, isSelected }) => {
    return {
      ...styles,
      backgroundColor: isFocused ? "rgba(0, 0, 0, 0.2)" : isSelected ? "rgba(0, 0, 0, 0.3)" : "transparent",
      color: "inherit",
      fontFamily: `"Source Sans Pro", "Roboto", "Arial", sans-serif`,
      fontWeight: 400,
      lineHeight: 2,
      fontSize: "1rem",
    };
  },
};

const UnstyledOption = ({ data, classes, isSelected, ...props }) => {
  const { Option: ReactSelectOption } = components;
  const { value, label } = data;

  return (
    <ReactSelectOption
      className={cn({ [classes.reactSelectOptionContainer]: isSelected })}
      value={value}
      data={data}
      {...props}
    >
      <span className={cn({ [classes.reactSelectOption]: isSelected }, "badge badge-light float-right")}>{label}</span>
    </ReactSelectOption>
  );
};

UnstyledOption.propTypes = {
  /**
   * Datos de la opción
   **/
  data: PropTypes.object.isRequired,
  /**
   * Indica si está seleccionada esta opción
   **/
  isSelected: PropTypes.bool.isRequired,
};

const Option = withStyles(styles)(UnstyledOption);

const getValue = ({ elements, value }) => (elements ? elements.find(option => option.value === value) : null);

const AutocompleteSelect = ({
  id,
  label,
  gqlQuery,
  getElementsFromDataHandler,
  classes,
  variables = {},
  value,
  ...props
}) => {
  const query = useMemo(() => getQuery({ gqlQuery, variables }), [gqlQuery, variables]);
  const { data, loading } = useQuery(query);

  const elements = getElementsFromDataHandler(data);

  return (
    <FormControl margin="none" fullWidth className={cn(classes.select_formcontrol)}>
      {id && label && (
        <InputLabel className={classes.select_label} htmlFor={id}>
          {label}
        </InputLabel>
      )}
      <ReactSelect
        components={{ Option }}
        value={getValue({ elements, value })}
        id={id}
        isClearable
        options={elements}
        isLoading={loading}
        styles={reactSelectStyles}
        {...props}
      />
    </FormControl>
  );
};

AutocompleteSelect.propTypes = {
  /**
   * Id del selector
   **/
  id: PropTypes.string,
  /**
   * Valor del selector
   **/
  value: PropTypes.string,
  /**
   * Label del selector
   **/
  label: PropTypes.string,
  /**
   * Parámetros de la query graphql
   **/
  variables: PropTypes.object.isRequired,
  /**
   * Query graphql que se utilizará para obtener los elementos
   **/
  gqlQuery: PropTypes.object.isRequired,
  /**
   * Función que devuelve la lista de elementos a partir de los datos de la query
   **/
  getElementsFromDataHandler: PropTypes.func.isRequired,
  /**
   * Placeholder del input del autocomplete.
   **/
  placeholder: PropTypes.string,
  /**
   * Callback que se llama cuando cambia el valor del selector.
   **/
  onChange: PropTypes.func,
};

export default withStyles(styles)(AutocompleteSelect);
